/* eslint-disable no-console */
interface LogContext {
	[key: string]: unknown;
	error?: unknown;
}

interface Logger {
	error: (message: string, context?: LogContext) => void;
	warn: (message: string, context?: LogContext) => void;
	info: (message: string, context?: LogContext) => void;
	debug: (message: string, context?: LogContext) => void;
}

type LogLevel = keyof Logger;

type ErrorRecord = Record<string, unknown>;

function isErrorRecord(value: unknown): value is ErrorRecord {
	return value !== null && typeof value === 'object';
}

function stringifyUnknown(value: unknown): string {
	if (typeof value === 'string') return value;
	if (
		typeof value === 'number' ||
		typeof value === 'boolean' ||
		typeof value === 'bigint' ||
		typeof value === 'symbol'
	) {
		return String(value);
	}
	if (typeof value === 'function') return '[Function]';

	const seen = new WeakSet<object>();
	const serialized = JSON.stringify(value, (_key, nestedValue) => {
		if (typeof nestedValue === 'bigint' || typeof nestedValue === 'symbol') {
			return String(nestedValue);
		}
		if (typeof nestedValue === 'function') return '[Function]';
		if (isErrorRecord(nestedValue)) {
			if (seen.has(nestedValue)) return '[Circular]';
			seen.add(nestedValue);
		}
		return nestedValue;
	});

	return serialized ?? String(value);
}

function collectErrorRecords(error: unknown): ErrorRecord[] {
	const root = isErrorRecord(error) ? error : undefined;
	if (!root) return [];

	const originalErrorObject = isErrorRecord(root.originalErrorObject)
		? root.originalErrorObject
		: undefined;
	const nestedOriginalErrorObject = isErrorRecord(
		originalErrorObject?.originalErrorObject
	)
		? (originalErrorObject.originalErrorObject as ErrorRecord)
		: undefined;
	const response = isErrorRecord(root.response)
		? root.response
		: isErrorRecord(originalErrorObject?.response)
			? (originalErrorObject.response as ErrorRecord)
			: undefined;

	return [
		root,
		originalErrorObject,
		nestedOriginalErrorObject,
		response
	].filter((record): record is ErrorRecord => !!record);
}

function pickFirstValue(
	records: ErrorRecord[],
	keys: string[]
): unknown | undefined {
	for (const record of records) {
		for (const key of keys) {
			const value = record[key];
			if (value !== undefined && value !== null && value !== '') return value;
		}
	}
}

function pickFirstString(
	records: ErrorRecord[],
	keys: string[]
): string | undefined {
	const value = pickFirstValue(records, keys);
	if (value === undefined) return undefined;
	return stringifyUnknown(value);
}

function pickFirstNumber(
	records: ErrorRecord[],
	keys: string[]
): number | undefined {
	const value = pickFirstValue(records, keys);
	if (typeof value === 'number') return value;
	if (typeof value === 'string') {
		const parsed = Number(value);
		return Number.isFinite(parsed) ? parsed : undefined;
	}
}

function extractUrl(value: unknown): string | undefined {
	if (value === undefined || value === null || value === '') return undefined;
	if (typeof value === 'string') return value;
	if (value instanceof URL) return value.toString();
	if (isErrorRecord(value) && typeof value.url === 'string') return value.url;
	return stringifyUnknown(value);
}

export function extractErrorLogContext(error: unknown): LogContext {
	const records = collectErrorRecords(error);
	const message =
		pickFirstString(records, ['message', 'statusMessage', 'statusText']) ??
		stringifyUnknown(error);
	const status = pickFirstNumber(records, ['statusCode', 'status']);
	const request = pickFirstValue(records, ['request', 'url']);
	const stack = pickFirstString(records, ['stack']);
	const name = pickFirstString(records, ['name']);
	const source = pickFirstString(records, ['source']);
	const statusMessage = pickFirstString(records, [
		'statusMessage',
		'statusText'
	]);
	const url = extractUrl(request);

	return Object.fromEntries(
		Object.entries({
			error_message: message,
			error_status: status,
			error_status_message: statusMessage,
			error_source: source,
			error_name: name,
			error_stack: stack,
			error_url: url
		}).filter(([, value]) => value !== undefined)
	);
}

function buildLogContext(
	context: LogContext | undefined,
	loggerName: string | undefined
): LogContext | undefined {
	const enrichedContext = { ...context };

	if (context && 'error' in context) {
		Object.assign(enrichedContext, extractErrorLogContext(context.error));
		delete enrichedContext.error;
	}

	if (loggerName) {
		enrichedContext.logger_name = loggerName;
	}

	return Object.keys(enrichedContext).length > 0 ? enrichedContext : undefined;
}

export function useLogger(service?: string): Logger {
	function createLogFn(level: LogLevel) {
		return (message: string, context?: LogContext) => {
			const enrichedContext = buildLogContext(context, service);

			if (import.meta.client && window.DD_LOGS) {
				window.DD_LOGS.logger[level](message, enrichedContext);
				return;
			}

			console[level](`[${service || 'App'}]`, message, enrichedContext);
		};
	}

	return {
		error: createLogFn('error'),
		warn: createLogFn('warn'),
		info: createLogFn('info'),
		debug: createLogFn('debug')
	};
}
